import { useSyncExternalStore } from 'react';
import { matchesPathSegments } from './navMatch';

/**
 * The v1/v2 portal toggle (Ari, 2026-10-07: "I can view the v2 pages, then
 * toggle in the sidebar to v1"). Only users who pass isV2PreviewEnabled ever
 * see it; the choice is per viewer, in localStorage, default v2. Storage can
 * throw (private mode, blocked site data), so every read and write is
 * guarded and a failure means the default.
 */
export type ShellView = 'v1' | 'v2';

export const SHELL_VIEW_KEY = 'gb.shellView';
const EVENT = 'gb:shell-view';

export function readShellView(): ShellView {
  try {
    return window.localStorage.getItem(SHELL_VIEW_KEY) === 'v1' ? 'v1' : 'v2';
  } catch {
    return 'v2';
  }
}

export function writeShellView(view: ShellView): void {
  try {
    window.localStorage.setItem(SHELL_VIEW_KEY, view);
    memory = null;
  } catch {
    // Not remembered across visits; the in-memory switch applies this session.
    memory = view;
  }
  window.dispatchEvent(new Event(EVENT));
}

// Holds the choice when storage refuses the write.
let memory: ShellView | null = null;

function snapshot(): ShellView {
  return memory ?? readShellView();
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === SHELL_VIEW_KEY) {
      memory = null;
      onChange();
    }
  };
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** The viewer's chosen portal view (v2 unless they switched to classic). */
export function useShellView(): ShellView {
  return useSyncExternalStore(subscribe, snapshot, () => 'v2');
}

/** Pages that exist in both portals keep their path. */
const SHARED = ['/reports', '/notifications', '/catalog', '/admin'];

/** Classic settings pages and the v2 Settings tab that covers each. */
const SETTINGS_TABS: Array<[string, string]> = [
  ['/team', 'team'],
  ['/company/exports', 'exports'],
  ['/profile', 'profile'],
];

/** Where "Classic portal" goes from a v2 page: its v1 equivalent, else the Intelligence Suite. */
export function v1Equivalent(pathname: string, search = ''): string {
  const on = (r: string) => matchesPathSegments(pathname, r);
  if (SHARED.some(on)) return pathname;
  if (SETTINGS_TABS.some(([r]) => on(r))) return pathname; // the classic page itself
  if (on('/settings')) {
    const tab = new URLSearchParams(search).get('tab');
    return SETTINGS_TABS.find(([, t]) => t === tab)?.[0] ?? '/profile';
  }
  if (on('/home')) return '/'; // the v1 landing for the user's role (RootRedirect)
  if (on('/sales')) return '/portal/total?tab=sales';
  if (on('/products')) return '/portal';
  if (on('/returns')) return '/portal/total?tab=returns';
  if (on('/finances')) return '/profit-loss';
  return '/portal';
}

/** Where "New portal" goes from a v1 page: its v2 equivalent, else Home. */
export function v2Equivalent(pathname: string, search = ''): string {
  const on = (r: string) => matchesPathSegments(pathname, r);
  if (SHARED.some(on)) return pathname;
  const settings = SETTINGS_TABS.find(([r]) => on(r));
  if (settings) return settings[1] === 'profile' ? '/settings' : `/settings?tab=${settings[1]}`;
  if (on('/portal/total')) {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'sales') return '/sales';
    if (tab === 'returns') return '/returns';
    if (tab === 'inventory') return '/products';
    return '/home';
  }
  if (on('/portal')) return '/products'; // Style View, Categories, a style's detail
  if (on('/profit-loss') || on('/reconcile')) return '/finances';
  if (on('/promotion')) return '/products'; // promotions live on Products now
  return '/home';
}
